import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  Text,
  Spinner,
  Combobox,
  Dropdown,
  Option,
  Badge,
  Avatar,
  Input,
  Textarea,
  Field,
  RadioGroup,
  Radio,
  Tooltip,
  Link,
  mergeClasses,
  useToastController,
  Toast,
  ToastTitle,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  CalendarLtr20Regular,
  Edit20Regular,
  Save20Regular,
  ChevronRight20Regular,
  ChevronDown20Regular,
  Open16Regular,
  Info16Regular,
  ErrorCircle16Filled,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BasicButton } from '@components/common/BasicButton';
import { InfoRow } from '@components/dashboard/InfoRow';
import { TimePicker } from '@components/common/TimePicker/TimePicker';
import { getRoomLayoutMeta } from '@components/spaces/SpaceCard/SpaceCard.constants';
import { PeoplePicker } from '@components/common/PeoplePicker';
import { VISIT_PURPOSE_OPTIONS, ATTENDEE_TYPE_OPTIONS } from '@config/bookingFormConfig';
import {
  formatUtcDateInTimeZone,
  formatUtcTimeInTimeZone,
  getIsoDateInTimeZone,
  isNoOccurrenceWindowOpen,
} from '@/utils/timeZone';
import { isSpaceBookable } from '@/utils/spaceAvailability';
import { telemetry } from '@/services/telemetry';
import { getActiveAccount } from '@/services';
import { EVENT_TYPES } from '@/constants';
import { useLazyGetEventHistoryQuery } from '@/store/api/eventService/eventServiceApi';
import { useCheckSpaceAvailabilityMutation } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import type { EventData, EventSpace, EventSpaceSelection } from '@/store/models/events/entities';
import type { EventHistoryEntry, FieldChange } from '@/store/models/events/entities';
import type { Building } from '@/store/models/buildings';
import type { SpaceWithAvailability } from '@/store/models/spaces';
import { asSpaceId, asBuildingId, asEventId } from '@/store/models/ids/branded-ids';
import type { EventReviewEditInput } from '@/types/planner.types';
import { SelectedSpacesEditor } from '@components/planner/SelectedSpacesEditor';
import { useEventReviewPanelStyles } from './EventReviewPanel.styles';

const TELEMETRY_COMPONENT = 'EventReviewPanel';

const EDITABLE_STATES = new Set(['SUBMITTED', 'UNDER_REVIEW', 'CONFIRMED', 'COMPLETED']);
const ACTIONABLE_STATE = 'UNDER_REVIEW';

const formatDateRange = (
  startDate: string,
  endDate: string,
  locale: string,
  timeZone?: string
): string => {
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  const startDateStr = formatUtcDateInTimeZone(startDate, timeZone);
  const startTime = formatUtcTimeInTimeZone(startDate, locale, timeZone, timeOpts);
  const endTime = formatUtcTimeInTimeZone(endDate, locale, timeZone, timeOpts);
  return `${startDateStr}, ${startTime} - ${endTime}`;
};

const yesNo = (val: boolean | undefined): string =>
  val === undefined ? 'N/A' : val ? 'Yes' : 'No';

const getFieldLabel = (fieldName: string, t: (key: string) => string): string => {
  const key = `reviewPanel.activities.fieldLabels.${fieldName}`;
  const translated = t(key);
  // If i18n returns the key itself, fall back to the field name
  return translated === key ? fieldName : translated;
};

const getTimeInputValue = (isoDateTime: string | undefined): string => {
  if (!isoDateTime) {
    return '';
  }

  const match = isoDateTime.match(/T(\d{2}:\d{2})/);
  return match?.[1] ?? '';
};

const getLocalDateInputValue = (isoDateTime: string | undefined, timeZone?: string): string => {
  if (!isoDateTime) {
    return '';
  }

  if (!timeZone) {
    return isoDateTime.split('T')[0] ?? '';
  }

  return getIsoDateInTimeZone(isoDateTime, timeZone);
};

const getLocalTimeInputValue = (isoDateTime: string | undefined, timeZone?: string): string => {
  if (!isoDateTime) {
    return '';
  }

  if (!timeZone) {
    return getTimeInputValue(isoDateTime);
  }

  return formatUtcTimeInTimeZone(isoDateTime, 'en-GB', timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const buildCalendarViewUrl = (eventStartDate?: string, eventId?: string): string => {
  const baseUrl = '/planner/calendar-view';
  const queryParts: string[] = [];
  // The calendar grid renders events using the browser's local machine
  // timezone (`new Date(...)`), so the deep-link date/time must be derived in
  // that same local timezone — not the building/event timezone. Otherwise a
  // late-night UTC event that crosses midnight when localized would link to the
  // wrong day (where the event isn't rendered).
  if (eventStartDate) {
    const d = new Date(eventStartDate);
    if (!Number.isNaN(d.getTime())) {
      const pad = (n: number): string => String(n).padStart(2, '0');
      const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      queryParts.push('view=day', `date=${dateStr}`, `time=${timeStr}`);
    }
  }
  if (eventId) {
    queryParts.push(`eventId=${encodeURIComponent(eventId)}`);
  }
  return queryParts.length > 0 ? `${baseUrl}?${queryParts.join('&')}` : baseUrl;
};

const extractSpaceName = (value: string | null | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as { spaceName?: unknown };
    if (parsed && typeof parsed.spaceName === 'string' && parsed.spaceName.trim().length > 0) {
      return parsed.spaceName;
    }
  } catch {
    // Not JSON — fall through to return undefined so the caller can decide on a fallback.
  }

  return undefined;
};

const DATE_TIME_FIELDS = new Set(['EventStartDate', 'EventEndDate']);

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isGuidValue = (value: string | null | undefined): boolean =>
  typeof value === 'string' && GUID_REGEX.test(value.trim());

// Field changes that should never appear in the activities list (case-insensitive):
// internal bookkeeping and timezone-localized date mirrors that duplicate the
// canonical EventStartDate/EventEndDate changes.
const HIDDEN_FIELD_CHANGES = new Set(['lastupdatedat', 'eventlocalstartdate', 'eventlocalenddate']);

// Fields whose values are structured JSON or are handled specially, and are therefore
// exempt from the generic raw-GUID filter (their GUIDs are never shown to the user).
const STRUCTURED_FIELD_CHANGES = new Set([
  'Space',
  'Spaces',
  'SelectedSpaces',
  'OnSiteContacts',
  'CalendarBookingId',
  'Sponsor',
]);

// Space-related Event properties the history diff can emit (single, plural, and the
// computed selection). They are consolidated into one readable entry, so they are never
// rendered as raw JSON field changes.
const SPACE_FIELD_NAMES = new Set(['Space', 'Spaces', 'SelectedSpaces']);

const shouldDisplayFieldChange = (fc: FieldChange): boolean => {
  if (HIDDEN_FIELD_CHANGES.has(fc.fieldName.toLowerCase())) {
    return false;
  }

  // Space-related fields are consolidated into a single readable entry (see deriveSpaceChange).
  if (SPACE_FIELD_NAMES.has(fc.fieldName)) {
    return false;
  }

  // Hide changes whose values are raw GUIDs — they carry no meaning for the reader.
  if (
    !STRUCTURED_FIELD_CHANGES.has(fc.fieldName) &&
    (isGuidValue(fc.previousValue) || isGuidValue(fc.newValue))
  ) {
    return false;
  }

  return true;
};

// On-site contacts are persisted as JSON. Only the name and email are meaningful to
// display in the activities list, so extract those and render a compact summary.
const formatOnSiteContacts = (value: string | null | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return undefined;
  }

  const contacts = Array.isArray(parsed) ? parsed : [parsed];
  const formatted = contacts
    .map((contact) => {
      if (!contact || typeof contact !== 'object') {
        return '';
      }
      const { name, email } = contact as { name?: unknown; email?: unknown };
      const nameStr = typeof name === 'string' ? name.trim() : '';
      const emailStr = typeof email === 'string' ? email.trim() : '';
      if (nameStr && emailStr) {
        return `${nameStr} (${emailStr})`;
      }
      return nameStr || emailStr;
    })
    .filter(Boolean);

  return formatted.length > 0 ? formatted.join('; ') : undefined;
};

// Sponsor is persisted as JSON ({ name, email, costCenter }). Render a readable
// summary instead of the raw JSON, e.g. "Pooja Gupta (pooja@contoso.com, costcenter: 12345678)".
const formatSponsor = (value: string | null | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return undefined;
  }

  if (!parsed || typeof parsed !== 'object') {
    return undefined;
  }

  const { name, email, costCenter } = parsed as {
    name?: unknown;
    email?: unknown;
    costCenter?: unknown;
  };

  const nameStr = typeof name === 'string' ? name.trim() : '';
  const emailStr = typeof email === 'string' ? email.trim() : '';
  const costCenterStr =
    typeof costCenter === 'string'
      ? costCenter.trim()
      : typeof costCenter === 'number'
        ? String(costCenter)
        : '';

  const details = [emailStr, costCenterStr ? `costcenter: ${costCenterStr}` : '']
    .filter(Boolean)
    .join(', ');

  if (nameStr && details) {
    return `${nameStr} (${details})`;
  }

  return nameStr || details || undefined;
};

const formatFieldChangeValue = (value: string | null, timeZone?: string): string => {
  if (!value) return '';
  if (isNaN(new Date(value).getTime())) return value;
  const dateStr = formatUtcDateInTimeZone(value, timeZone);
  const timeStr = formatUtcTimeInTimeZone(value, 'en-US', timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateStr}, ${timeStr}`;
};

const formatFieldChangeDetail = (
  fc: FieldChange,
  t: (key: string, options?: Record<string, unknown>) => string,
  timeZone?: string
): string => {
  if (fc.fieldName === 'Space') {
    const spaceName = extractSpaceName(fc.newValue) ?? extractSpaceName(fc.previousValue);
    const label = getFieldLabel(fc.fieldName, t);
    return spaceName ? `${label}: ${spaceName}` : label;
  }

  if (fc.fieldName === 'CalendarBookingId') {
    return t('reviewPanel.activities.calendarBookingUpdated');
  }

  if (fc.fieldName === 'OnSiteContacts') {
    const label = getFieldLabel(fc.fieldName, t);
    const summary = formatOnSiteContacts(fc.newValue) ?? formatOnSiteContacts(fc.previousValue);
    return summary ? `${label}: ${summary}` : label;
  }

  if (fc.fieldName === 'Sponsor') {
    const label = getFieldLabel(fc.fieldName, t);
    const summary = formatSponsor(fc.newValue) ?? formatSponsor(fc.previousValue);
    return summary ? `${label}: ${summary}` : label;
  }

  const label = getFieldLabel(fc.fieldName, t);

  if (DATE_TIME_FIELDS.has(fc.fieldName)) {
    const prev = formatFieldChangeValue(fc.previousValue, timeZone);
    const next = formatFieldChangeValue(fc.newValue, timeZone);
    return `${label}: ${prev} → ${next}`;
  }

  const prev = fc.previousValue ?? '';
  const next = fc.newValue ?? '';
  return `${label}: ${prev} → ${next}`;
};

interface SpaceLineItem {
  name: string;
  setup?: string;
}

interface SpaceChangeInfo {
  label: string;
  items: SpaceLineItem[];
  isMultiple: boolean;
  labelOnly: boolean;
}

const parseEventSpaces = (value: string | null | undefined): EventSpace[] | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(value);
    const list: unknown[] = Array.isArray(parsed) ? parsed : [parsed];
    return list.filter((item): item is EventSpace => !!item && typeof item === 'object');
  } catch {
    return undefined;
  }
};

const formatSpaceSetup = (codes: string[] | undefined): string | undefined => {
  if (!codes || codes.length === 0) {
    return undefined;
  }
  const labels = codes
    .map((code) => getRoomLayoutMeta(code).title)
    .filter((title) => title.trim().length > 0);
  return labels.length > 0 ? labels.join(', ') : undefined;
};

// Consolidates the Space/Spaces/SelectedSpaces field changes of a history entry into a
// single readable descriptor. Routing is by the resulting space count: one space keeps the
// legacy name-only label; two or more render as a bulleted list of name + room setup.
const deriveSpaceChange = (
  fieldChanges: FieldChange[] | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): SpaceChangeInfo | undefined => {
  if (!fieldChanges || fieldChanges.length === 0) {
    return undefined;
  }
  const spaceFc =
    fieldChanges.find((fc) => fc.fieldName === 'Spaces') ??
    fieldChanges.find((fc) => fc.fieldName === 'SelectedSpaces') ??
    fieldChanges.find((fc) => fc.fieldName === 'Space');
  if (!spaceFc) {
    return undefined;
  }

  const spaces = parseEventSpaces(spaceFc.newValue) ?? parseEventSpaces(spaceFc.previousValue);
  const sourceIsPlural = spaceFc.fieldName !== 'Space';

  if (!spaces || spaces.length === 0) {
    return {
      label: getFieldLabel(sourceIsPlural ? 'Spaces' : 'Space', t),
      items: [],
      isMultiple: false,
      labelOnly: true,
    };
  }

  const items: SpaceLineItem[] = spaces.map((space) => {
    const name =
      typeof space.spaceName === 'string' && space.spaceName.trim().length > 0
        ? space.spaceName.trim()
        : t('reviewPanel.activities.unnamedSpace');
    return { name, setup: formatSpaceSetup(space.selectedSetupCodes) };
  });

  const isMultiple = items.length >= 2;
  return {
    label: getFieldLabel(isMultiple ? 'Spaces' : 'Space', t),
    items,
    isMultiple,
    labelOnly: false,
  };
};

const renderSpaceChange = (change: SpaceChangeInfo, styles: Record<string, string>) => {
  if (change.labelOnly || change.items.length === 0) {
    return <Text className={styles.activityDescription}>{change.label}</Text>;
  }

  if (!change.isMultiple) {
    const [only] = change.items;
    return (
      <Text className={styles.activityDescription}>
        {only ? `${change.label}: ${only.name}` : change.label}
      </Text>
    );
  }

  return (
    <div className={styles.activitySpaceChange}>
      <Text className={styles.activityDescription}>{change.label}</Text>
      <ul className={styles.activitySpaceList}>
        {change.items.map((item, idx) => (
          <li key={idx} className={styles.activitySpaceListItem}>
            {item.setup ? `${item.name} (${item.setup})` : item.name}
          </li>
        ))}
      </ul>
    </div>
  );
};

const getVisitPurposeLabel = (codeOrLabel: string | undefined): string => {
  if (!codeOrLabel) {
    return '';
  }

  const match = VISIT_PURPOSE_OPTIONS.find((option) => option.value === codeOrLabel);
  return match?.labelKey ?? codeOrLabel;
};

const getAttendeeTypeLabel = (codeOrLabel: string | undefined): string => {
  if (!codeOrLabel) {
    return '';
  }

  const match = ATTENDEE_TYPE_OPTIONS.find((option) => option.value === codeOrLabel);
  return match?.labelKey ?? codeOrLabel;
};

const getBusinessGroupLabel = (
  codeOrLabel: string | undefined,
  building: Building | undefined
): string => {
  if (!codeOrLabel) {
    return '';
  }

  const match = building?.businessGroupConfig?.businessGroups?.find(
    (group) => group.code === codeOrLabel
  );
  return match?.label ?? codeOrLabel;
};

const getAvailabilityBadgeProps = (isAvailable: boolean | undefined) => {
  if (isAvailable === true) {
    return { color: 'success' as const, label: 'reviewPanel.availabilityBadge.available' };
  }

  if (isAvailable === false) {
    return { color: 'danger' as const, label: 'reviewPanel.availabilityBadge.booked' };
  }

  return { color: 'warning' as const, label: 'reviewPanel.availabilityBadge.unavailable' };
};

const getPreferredSetupCodes = (event: EventData | undefined): string[] => {
  const setupCodes = event?.spaces?.[0]?.selectedSetupCodes;
  if (setupCodes && setupCodes.length > 0) {
    return setupCodes;
  }

  return [];
};

const normalizeText = (value: string | undefined): string => value?.trim().toLowerCase() ?? '';

const normalizeSetupCode = (value: string | undefined): string =>
  value
    ?.trim()
    .toLowerCase()
    .replace(/[-_\s]/g, '') ?? '';

const resolveCurrentSpace = (
  spaces: SpaceWithAvailability[] | undefined,
  currentSpaceId: string,
  event: EventData | undefined
): SpaceWithAvailability | undefined => {
  if (!spaces || spaces.length === 0) {
    return undefined;
  }

  const idCandidates = [currentSpaceId, event?.spaces?.[0]?.spaceId, event?.spaceId]
    .map((value) => normalizeText(value))
    .filter(Boolean);

  for (const candidate of idCandidates) {
    const matchedById = spaces.find((space) => normalizeText(space.spaceId) === candidate);
    if (matchedById) {
      return matchedById;
    }
  }

  const nameCandidates = [event?.spaces?.[0]?.spaceName, event?.spaceName]
    .map((value) => normalizeText(value))
    .filter(Boolean);

  for (const candidate of nameCandidates) {
    const matchedByName = spaces.find((space) => normalizeText(space.spaceName) === candidate);
    if (matchedByName) {
      return matchedByName;
    }
  }

  return undefined;
};

interface EditFormState {
  stateProvince: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  attendees: string;
  roomSetupCodes: string[];
  attendeeType: string;
  businessPurpose: string;
  businessGroupCode: string;
  businessJustification: string;
  additionalInformation: string;
  ioCcNumber: string;
  hasOnSiteContact: boolean;
  onSiteContacts: { name: string; email: string }[];
  hasCatering: boolean;
  filmingRequired: boolean;
  overnightStorage: boolean;
  shipments: boolean;
  sponsorName: string;
  sponsorEmail: string;
}

const EDIT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MAX_ONSITE_CONTACTS = 10;

// Basic required/email validation for the planner edit form. Returns a map of
// field keys to localized messages; an empty map means the form is valid.
function getEditFormValidationErrors(
  form: EditFormState,
  options: {
    requiresCostCenter: boolean;
    requiresBusinessGroup: boolean;
    roomSetupRequired: boolean;
  },
  messages: { required: string; invalidEmail: string }
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!form.startDate.trim()) {
    errors.startDate = messages.required;
  }

  if (!form.startTime.trim() || !form.endTime.trim()) {
    errors.duration = messages.required;
  }

  const attendeesCount = Number(form.attendees);
  if (!form.attendees.trim() || Number.isNaN(attendeesCount) || attendeesCount <= 0) {
    errors.attendees = messages.required;
  }

  if (options.roomSetupRequired && form.roomSetupCodes.length === 0) {
    errors.roomSetup = messages.required;
  }

  if (!form.attendeeType.trim()) {
    errors.attendeeType = messages.required;
  }

  if (!form.businessPurpose.trim()) {
    errors.businessPurpose = messages.required;
  }

  if (options.requiresBusinessGroup) {
    if (!form.businessGroupCode.trim()) {
      errors.businessGroup = messages.required;
    }
    if (!form.businessJustification.trim()) {
      errors.businessJustification = messages.required;
    }
  }

  if (!form.sponsorName.trim()) {
    errors.sponsor = messages.required;
  }

  if (options.requiresCostCenter && !form.ioCcNumber.trim()) {
    errors.ioCcNumber = messages.required;
  }

  if (form.hasOnSiteContact) {
    form.onSiteContacts.forEach((contact, idx) => {
      if (!contact.name.trim()) {
        errors[`onSiteContactName-${idx}`] = messages.required;
      }
      const email = contact.email.trim();
      if (!email) {
        errors[`onSiteContactEmail-${idx}`] = messages.required;
      } else if (!EDIT_EMAIL_REGEX.test(email)) {
        errors[`onSiteContactEmail-${idx}`] = messages.invalidEmail;
      }
    });
  }

  return errors;
}

export interface EventReviewPanelProps {
  open: boolean;
  event: EventData | undefined;
  displayTitle?: string;
  building: Building | undefined;
  spaces: SpaceWithAvailability[] | undefined;
  buildings?: Building[];
  selectedSpaceStateProvince?: string;
  isLoading: boolean;
  isError: boolean;
  selectedSpaceId: string | null;
  selectedSpaces?: EventSpaceSelection[];
  onSelectedSpacesChange?: (spaces: EventSpaceSelection[]) => void;
  isConfirming: boolean;
  isDeclining: boolean;
  isMarkingNoOccurrence?: boolean;
  isSavingEdit?: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
  onDeclineClick: () => void;
  onCancelClick?: () => void;
  onNoOccurrenceClick?: () => void;
  onSaveEdit?: (input: EventReviewEditInput) => Promise<boolean>;
  onSpaceChange: (spaceId: string) => void | Promise<boolean>;
  onClearSpaceChange?: () => void;
  onFetchSpaces?: (dateTimeOverrides?: {
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    buildingIds?: string[];
  }) => void;
  isSearchingSpaces?: boolean;
  onResendSurvey?: () => Promise<boolean>;
  isResendingSurvey?: boolean;
  onRetry: () => void;
  initialEditing?: boolean;
  toasterId?: string;
}

export const EventReviewPanel = ({
  open,
  event,
  displayTitle,
  building,
  spaces,
  buildings,
  selectedSpaceStateProvince,
  isLoading,
  isError: _isError,
  selectedSpaceId,
  selectedSpaces,
  onSelectedSpacesChange,
  isConfirming,
  isMarkingNoOccurrence = false,
  isSavingEdit = false,
  error: _error,
  onClose,
  onConfirm,
  onDeclineClick,
  onCancelClick,
  onNoOccurrenceClick,
  onSaveEdit,
  onSpaceChange,
  onClearSpaceChange,
  onFetchSpaces,
  isSearchingSpaces = false,
  onResendSurvey,
  isResendingSurvey = false,
  onRetry: _onRetry,
  initialEditing = false,
  toasterId,
}: EventReviewPanelProps) => {
  const { dispatchToast } = useToastController(toasterId);
  const styles = useEventReviewPanelStyles();
  const { t, i18n } = useTranslation(['planner', 'spaceCard']);
  const navigate = useNavigate();
  const [activitiesExpanded, setActivitiesExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAllSpaces, setShowAllSpaces] = useState(false);
  const [noOccurrenceDialogOpen, setNoOccurrenceDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const initialEditFormRef = useRef<EditFormState | null>(null);
  const [spaceQuery, setSpaceQuery] = useState<string | undefined>(undefined);
  const [localSelectedSpace, setLocalSelectedSpace] = useState<SpaceWithAvailability | undefined>(
    undefined
  );
  const [selectedEditBuildingId, setSelectedEditBuildingId] = useState<string | undefined>(
    undefined
  );
  const [buildingSearchQuery, setBuildingSearchQuery] = useState<string | undefined>(undefined);
  const [checkSpaceAvailability] = useCheckSpaceAvailabilityMutation();
  const [realTimeAvailability, setRealTimeAvailability] = useState<boolean | undefined>(undefined);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  const stateProvinceValue =
    selectedSpaceStateProvince ??
    building?.stateName ??
    building?.stateCode ??
    event?.building?.gmtZone ??
    '';
  const eventTimeZone = building?.gmtZone ?? event?.building?.gmtZone;
  const [, refreshEndBoundary] = useState(0);
  const isPastEvent = !!event?.eventEndDate && Date.now() > Date.parse(event.eventEndDate);

  // Freeze an already-open form as soon as the original event ends.
  useEffect(() => {
    const endTime = Date.parse(event?.eventEndDate ?? '');
    if (!Number.isFinite(endTime) || Date.now() > endTime) return;

    let timer: ReturnType<typeof setTimeout>;
    const scheduleBoundaryRefresh = () => {
      const remaining = endTime - Date.now();
      if (remaining < 0) {
        refreshEndBoundary((value) => value + 1);
        return;
      }

      // Longer delays overflow browser timers; chain capped delays until just after end.
      timer = setTimeout(scheduleBoundaryRefresh, Math.min(remaining + 1, 2_147_483_647));
    };
    scheduleBoundaryRefresh();
    return () => clearTimeout(timer);
  }, [event?.eventEndDate]);

  const activitiesSentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const spaceFetchedRef = useRef<boolean>(false);
  const initialEditingAppliedRef = useRef(false);
  const [
    triggerGetHistory,
    {
      data: historyPage,
      isLoading: isHistoryLoading,
      isError: isHistoryError,
      isFetching: isHistoryFetching,
    },
  ] = useLazyGetEventHistoryQuery();
  const [historyEntries, setHistoryEntries] = useState<EventHistoryEntry[]>([]);
  const [, setHistoryTotalCount] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const PAGE_SIZE = 20;

  // Accumulate paginated results
  useEffect(() => {
    if (!historyPage) return;
    setHistoryTotalCount(historyPage.totalCount);
    setHistoryHasMore(historyPage.hasMore);
    if (historyPage.skip === 0) {
      setHistoryEntries(historyPage.items);
    } else {
      setHistoryEntries((prev) => [...prev, ...historyPage.items]);
    }
  }, [historyPage]);

  // Fetch the first page and seed entries directly from the resolved result.
  // RTK Query's structural sharing can return the previously cached page with an
  // unchanged reference when the activities section is reopened for the same event,
  // in which case the historyPage effect above never re-runs and the list renders
  // empty even though the request succeeded. Reading the resolved value here
  // guarantees the first page always populates.
  const triggerFirstHistoryPage = useCallback(() => {
    if (!event) return;
    setHistoryLoading(true);
    const result = triggerGetHistory(
      {
        eventId: event.eventId as string,
        buildingId: event.buildingId as string,
        skip: 0,
        top: PAGE_SIZE,
      },
      false
    );
    const unwrap = (
      result as {
        unwrap?: () => Promise<{
          items: EventHistoryEntry[];
          totalCount: number;
          skip: number;
          top: number;
          hasMore: boolean;
        }>;
      }
    )?.unwrap;
    if (typeof unwrap !== 'function') {
      // No unwrap available (e.g. trigger returned a non-thenable); avoid leaving
      // the loading state stuck on forever.
      setHistoryLoading(false);
      return;
    }
    void unwrap()
      .then((page) => {
        if (page.skip === 0) {
          setHistoryTotalCount(page.totalCount);
          setHistoryHasMore(page.hasMore);
          setHistoryEntries(page.items);
        }
      })
      .catch(() => {
        // Error state is surfaced via isHistoryError from the query hook.
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  }, [event, triggerGetHistory]);

  const handleEditClick = useCallback(() => {
    if (
      !event ||
      !EDITABLE_STATES.has(event.eventState) ||
      !event.plannerAssignedEmail ||
      getActiveAccount()?.username?.toLowerCase() !== event.plannerAssignedEmail.toLowerCase()
    ) return;
    const setupCodes = getPreferredSetupCodes(event);
    const formState: EditFormState = {
      stateProvince: stateProvinceValue,
      startDate: getLocalDateInputValue(event.eventStartDate, eventTimeZone),
      endDate: getLocalDateInputValue(event.eventEndDate, eventTimeZone),
      startTime: getLocalTimeInputValue(event.eventStartDate, eventTimeZone),
      endTime: getLocalTimeInputValue(event.eventEndDate, eventTimeZone),
      attendees: String(event.numberOfAttendees),
      roomSetupCodes: setupCodes,
      attendeeType: event.attendeeTypeCode ?? '',
      businessPurpose: event.visitPurposeCode ?? '',
      businessGroupCode: event.businessGroupCode ?? '',
      businessJustification: event.businessJustification ?? '',
      additionalInformation: event.additionalInformation ?? '',
      ioCcNumber: event.sponsor?.costCenter ?? '',
      hasOnSiteContact: event.hasOnSiteContact ?? (event.onSiteContacts?.length ? true : false),
      onSiteContacts: event.onSiteContacts?.length
        ? event.onSiteContacts.map((c) => ({ name: c.name, email: c.email }))
        : [{ name: '', email: '' }],
      hasCatering: event.hasCatering ?? false,
      filmingRequired: event.filmingRequired ?? false,
      overnightStorage: event.overnightStorageRequired ?? false,
      shipments: event.shipmentsExpected ?? false,
      sponsorName: event.sponsor?.name ?? '',
      sponsorEmail: event.sponsor?.email ?? '',
    };
    setEditForm(formState);
    initialEditFormRef.current = formState;
    setShowValidationErrors(false);
    setIsEditing(true);
    const bid = event.buildingId ?? event.building?.buildingId;
    setSelectedEditBuildingId(bid);
    if (bid) {
      const tz = building?.gmtZone ?? event?.building?.gmtZone;
      const startDate = getLocalDateInputValue(event.eventStartDate, tz);
      const endDate = getLocalDateInputValue(event.eventEndDate, tz);
      const startTime = getLocalTimeInputValue(event.eventStartDate, tz);
      const endTime = getLocalTimeInputValue(event.eventEndDate, tz);
      onFetchSpaces?.(
        Date.now() > Date.parse(event.eventEndDate)
          ? { buildingIds: [bid] }
          : { startDate, endDate, startTime, endTime, buildingIds: [bid] }
      );
    }
  }, [event, stateProvinceValue, eventTimeZone, building, onFetchSpaces]);

  const isFormDirty = useMemo(() => {
    const initial = initialEditFormRef.current;
    if (!editForm || !initial) return false;
    return (
      editForm.startDate !== initial.startDate ||
      editForm.endDate !== initial.endDate ||
      editForm.startTime !== initial.startTime ||
      editForm.endTime !== initial.endTime ||
      editForm.attendees !== initial.attendees ||
      editForm.roomSetupCodes.join(',') !== initial.roomSetupCodes.join(',') ||
      editForm.attendeeType !== initial.attendeeType ||
      editForm.businessPurpose !== initial.businessPurpose ||
      editForm.businessGroupCode !== initial.businessGroupCode ||
      editForm.businessJustification !== initial.businessJustification ||
      editForm.additionalInformation !== initial.additionalInformation ||
      editForm.ioCcNumber !== initial.ioCcNumber ||
      editForm.hasOnSiteContact !== initial.hasOnSiteContact ||
      editForm.hasCatering !== initial.hasCatering ||
      editForm.filmingRequired !== initial.filmingRequired ||
      editForm.overnightStorage !== initial.overnightStorage ||
      editForm.shipments !== initial.shipments ||
      editForm.sponsorName !== initial.sponsorName ||
      editForm.sponsorEmail !== initial.sponsorEmail ||
      JSON.stringify(editForm.onSiteContacts) !== JSON.stringify(initial.onSiteContacts)
    );
  }, [editForm]);

  const hasSpacesChanged = useMemo(() => {
    if (!selectedSpaces || !event) return false;
    const originalSpaces = event.spaces ?? [];
    if (originalSpaces.length !== selectedSpaces.length) return true;
    return selectedSpaces.some((s, i) => {
      const orig = originalSpaces[i];
      return (
        s.spaceId !== orig?.spaceId ||
        (s.selectedSetupCodes ?? []).join(',') !== (orig?.selectedSetupCodes ?? []).join(',')
      );
    });
  }, [selectedSpaces, event]);

  const hasUnsavedChanges = isFormDirty || hasSpacesChanged;

  const currentSpaceId = selectedSpaceId ?? event?.spaces?.[0]?.spaceId ?? event?.spaceId ?? '';

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    // Don't overwrite state/province when user has manually selected a different space
    // and spaces are being re-fetched (which temporarily resets selectedSpaceStateProvince)
    if (localSelectedSpace && isSearchingSpaces) {
      return;
    }

    setEditForm((prev) => (prev ? { ...prev, stateProvince: stateProvinceValue } : prev));
  }, [isEditing, stateProvinceValue, localSelectedSpace, isSearchingSpaces]);

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setEditForm(null);
      setShowValidationErrors(false);
      setLocalSelectedSpace(undefined);
      setRealTimeAvailability(undefined);
      setHistoryFetched(false);
      setHistoryEntries([]);
      setHistoryTotalCount(0);
      setHistoryHasMore(false);
      setHistoryLoading(false);
      setActivitiesExpanded(false);
    }
  }, [open]);

  // Fetch real-time availability for the effective space whenever the space or date/time changes in edit mode
  const fetchSpaceAvailability = useCallback(
    async (
      spaceId: string,
      buildingId: string,
      startDate: string,
      endDate: string,
      startTime: string,
      endTime: string,
      eventId?: string
    ) => {
      if (!spaceId || !buildingId || !startDate || !startTime || !endTime) return;
      setIsCheckingAvailability(true);
      try {
        const result = await checkSpaceAvailability({
          spaceId: asSpaceId(spaceId),
          body: {
            buildingId: asBuildingId(buildingId),
            startDate,
            endDate: endDate || startDate,
            startTime,
            endTime,
            excludeEventId: eventId ? asEventId(eventId) : undefined,
          },
        }).unwrap();
        setRealTimeAvailability(result.data?.isAvailable);
      } catch {
        setRealTimeAvailability(undefined);
      } finally {
        setIsCheckingAvailability(false);
      }
    },
    [checkSpaceAvailability]
  );

  // Re-check space availability when planner changes date or time in edit mode (debounced)
  const prevDateTimeRef = useRef<string | null>(null);
  const recheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isEditing || !editForm) return;
    const { startDate, endDate, startTime, endTime } = editForm;
    if (!startDate || !startTime || !endTime) return;
    const key = `${startDate}-${endDate}-${startTime}-${endTime}`;
    if (prevDateTimeRef.current === null) {
      // First render after entering edit mode — skip (initial fetch already happened)
      prevDateTimeRef.current = key;
      return;
    }
    if (prevDateTimeRef.current === key) return;
    prevDateTimeRef.current = key;

    // Debounce 500ms so rapid changes don't fire many API calls
    if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
    recheckTimerRef.current = setTimeout(() => {
      onFetchSpaces?.({
        startDate,
        endDate,
        startTime,
        endTime,
        buildingIds: selectedEditBuildingId ? [selectedEditBuildingId] : [],
      });
    }, 500);

    return () => {
      if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditing,
    editForm?.startDate,
    editForm?.endDate,
    editForm?.startTime,
    editForm?.endTime,
    onFetchSpaces,
  ]);

  useEffect(() => {
    if (!isEditing) {
      prevDateTimeRef.current = null;
    }
  }, [isEditing]);

  // Lazy-load first page via IntersectionObserver when activities section is visible
  useEffect(() => {
    if (!activitiesExpanded || !event || historyFetched) return;

    const sentinel = activitiesSentinelRef.current;
    if (!sentinel) {
      // Sentinel not yet rendered; fetch immediately on expand
      triggerFirstHistoryPage();
      setHistoryFetched(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          triggerFirstHistoryPage();
          setHistoryFetched(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activitiesExpanded, event, historyFetched, triggerFirstHistoryPage]);

  // Auto-load next page when "load more" sentinel scrolls into view
  useEffect(() => {
    if (!historyHasMore || isHistoryFetching || !event) return;
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isHistoryFetching) {
          void triggerGetHistory(
            {
              eventId: event.eventId as string,
              buildingId: event.buildingId as string,
              skip: historyEntries.length,
              top: PAGE_SIZE,
            },
            false
          );
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [historyHasMore, isHistoryFetching, event, historyEntries.length, triggerGetHistory]);

  useEffect(() => {
    if (!open || !initialEditing) {
      initialEditingAppliedRef.current = false;
      return;
    }

    if (event && !initialEditingAppliedRef.current) {
      initialEditingAppliedRef.current = true;
      handleEditClick();
    }
  }, [open, initialEditing, event, handleEditClick]);

  // Fetch spaces in view mode for the event's building to get room layout capacity data
  useEffect(() => {
    if (!open || !event || isEditing || spaceFetchedRef.current || !onFetchSpaces) return;

    const bid = event.buildingId ?? event.building?.buildingId;
    if (!bid) return;

    spaceFetchedRef.current = true;
    onFetchSpaces({ buildingIds: [bid] });
  }, [open, event, isEditing, building, onFetchSpaces]);

  useEffect(() => {
    if (open && event) {
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
        action: 'ReviewPanelOpened',
        eventId: event.eventId as string,
      });
    }
  }, [open, event]);

  useEffect(() => {
    // Reset space fetch flag when panel closes
    if (!open) {
      spaceFetchedRef.current = false;
    }
  }, [open]);

  const handleClose = useCallback(() => {
    if (event) {
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
        action: 'ReviewPanelClosed',
        eventId: event.eventId as string,
      });
    }
    onClose();
  }, [onClose, event]);

  const handleViewInCalendarClick = useCallback(() => {
    handleClose();
    void navigate(buildCalendarViewUrl(event?.eventStartDate, event?.eventId));
  }, [handleClose, navigate, event?.eventStartDate, event?.eventId]);

  const isActionable = event?.eventState === ACTIONABLE_STATE;
  const isCancellable =
    event?.eventState === 'CONFIRMED' || event?.eventState === 'SUBMITTED_FOR_CANCELLATION';
  const isConfirmed = event?.eventState === 'CONFIRMED';
  const isCompleted = event?.eventState === 'COMPLETED';
  const isNoOccurrenceEligible =
    isCompleted && isNoOccurrenceWindowOpen(event?.eventEndDate, eventTimeZone);
  const isEditable = event ? EDITABLE_STATES.has(event.eventState) : false;

  const isAssignedPlanner = useMemo(() => {
    const assignedEmail = (event as { plannerAssignedEmail?: string } | undefined)
      ?.plannerAssignedEmail;
    if (!assignedEmail) return false;
    const currentUser = getActiveAccount();
    return currentUser?.username?.toLowerCase() === assignedEmail.toLowerCase();
  }, [event]);

  const isEndedOver24HoursAgo = useMemo(() => {
    if (!event?.eventEndDate) return false;
    return Date.now() - new Date(event.eventEndDate).getTime() >= 86_400_000;
  }, [event?.eventEndDate]);
  const canResendSurvey = isCompleted && isEndedOver24HoursAgo && isAssignedPlanner && !isEditing;

  const handleResendSurvey = useCallback(() => {
    if (!onResendSurvey) return;

    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
      action: 'ResendSurveyClicked',
      eventId: (event?.eventId as string) ?? '',
    });

    void onResendSurvey().then((success) => {
      if (success && dispatchToast) {
        dispatchToast(
          <Toast>
            <ToastTitle>{t('reviewPanel.footer.resendSurveySuccess')}</ToastTitle>
          </Toast>,
          { intent: 'success', timeout: 3000 }
        );
      }
    });
  }, [onResendSurvey, event?.eventId, dispatchToast, t]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditForm(null);
    setShowValidationErrors(false);
    setLocalSelectedSpace(undefined);
    setRealTimeAvailability(undefined);
    setSpaceQuery(undefined);
    // Reset the parent's staged space selection so read-only view shows original space
    onClearSpaceChange?.();
    // Reset multi-space selection to original event data
    if (onSelectedSpacesChange && event) {
      if (event.spaces && event.spaces.length > 0) {
        onSelectedSpacesChange(event.spaces);
      } else if (event.spaceId) {
        onSelectedSpacesChange([
          {
            spaceId: event.spaceId,
            spaceName: event.spaceName ?? '',
            selectedSetupCodes: [],
          },
        ]);
      }
    }
  }, [onClearSpaceChange, onSelectedSpacesChange, event]);

  const handleSaveEdit = useCallback(async () => {
    if (!editForm) {
      return;
    }

    const requiresCostCenter =
      (buildings?.find((candidate) => candidate.buildingId === selectedEditBuildingId) ?? building)
        ?.requireCostCenter === true;
    const errors = getEditFormValidationErrors(
      editForm,
      {
        requiresCostCenter,
        requiresBusinessGroup: building?.businessGroupConfig?.required === true,
        roomSetupRequired: !isPastEvent && !(selectedSpaces && onSelectedSpacesChange),
      },
      {
        required: t('reviewPanel.validation.required'),
        invalidEmail: t('reviewPanel.validation.invalidEmail'),
      }
    );
    if (Object.keys(errors).length > 0) {
      setShowValidationErrors(true);
      return;
    }

    if (!onSaveEdit) {
      setIsEditing(false);
      setEditForm(null);
      return;
    }

    const didSave = await onSaveEdit(editForm);
    if (didSave) {
      setIsEditing(false);
      setEditForm(null);
      // Collapse the activities panel and reset its fetch state so the timeline
      // re-fetches fresh history (including the new edit's audit entry) next time
      // it is expanded. Clear the cached entries/counts too so stale history isn't
      // shown momentarily before the refetch completes.
      setActivitiesExpanded(false);
      setHistoryFetched(false);
      setHistoryEntries([]);
      setHistoryTotalCount(0);
      setHistoryHasMore(false);
    }
  }, [
    editForm,
    onSaveEdit,
    buildings,
    selectedEditBuildingId,
    building,
    selectedSpaces,
    onSelectedSpacesChange,
    isPastEvent,
    t,
  ]);

  const currentSpace = resolveCurrentSpace(spaces, currentSpaceId, event);
  const selectedPlannerSpace = localSelectedSpace ?? currentSpace;

  const effectiveSelectedSpaceId = selectedPlannerSpace?.spaceId ?? currentSpaceId;
  const selectedEditBuilding = buildings?.find(
    (candidate) => candidate.buildingId === selectedEditBuildingId
  );
  const showCostCenter =
    (isEditing ? (selectedEditBuilding ?? building) : building)?.requireCostCenter === true;
  const editFormValidationErrors = useMemo(
    () =>
      editForm && showValidationErrors
        ? getEditFormValidationErrors(
            editForm,
            {
              requiresCostCenter: showCostCenter,
              requiresBusinessGroup: building?.businessGroupConfig?.required === true,
              roomSetupRequired: !isPastEvent && !(selectedSpaces && onSelectedSpacesChange),
            },
            {
              required: t('reviewPanel.validation.required'),
              invalidEmail: t('reviewPanel.validation.invalidEmail'),
            }
          )
        : {},
    [
      editForm,
      showValidationErrors,
      showCostCenter,
      building,
      selectedSpaces,
      onSelectedSpacesChange,
      isPastEvent,
      t,
    ]
  );
  const hasEditValidationErrors = Object.keys(editFormValidationErrors).length > 0;
  const showBusinessGroup = building?.businessGroupConfig?.required === true;
  const businessGroupOptions = building?.businessGroupConfig?.businessGroups ?? [];
  const setupCodes = getPreferredSetupCodes(event);
  const shouldDefaultBoardroomSetup =
    setupCodes.length === 0 &&
    selectedPlannerSpace?.isBoardRoom === true &&
    (selectedPlannerSpace.supportedRoomLayouts?.length ?? 0) === 0;
  const effectiveSetupCodes = useMemo(
    () => (shouldDefaultBoardroomSetup ? ['boardroom'] : setupCodes),
    [shouldDefaultBoardroomSetup, setupCodes]
  );
  const setupOptions = useMemo(() => {
    const options = selectedPlannerSpace?.supportedRoomLayouts ?? [];
    if (options.length === 0) {
      if (selectedPlannerSpace?.isBoardRoom) {
        return [{ code: 'boardroom', capacity: 0 }];
      }

      return effectiveSetupCodes.map((code) => ({ code, capacity: 0 }));
    }

    const mergedOptions = [...options];
    const seenCodes = new Set(options.map((layout) => normalizeSetupCode(layout.code)));

    for (const code of effectiveSetupCodes) {
      const normalizedCode = normalizeSetupCode(code);
      if (!normalizedCode || seenCodes.has(normalizedCode)) {
        continue;
      }

      mergedOptions.push({ code, capacity: 0 });
      seenCodes.add(normalizedCode);
    }

    return mergedOptions;
  }, [selectedPlannerSpace, effectiveSetupCodes]);
  const roomSetupDisplayValue = useMemo(
    () =>
      effectiveSetupCodes
        .map((code) => {
          const normalizedCode = normalizeSetupCode(code);
          const layout = setupOptions.find(
            (candidate) => normalizeSetupCode(candidate.code) === normalizedCode
          );
          const title = getRoomLayoutMeta(code).title;
          return layout && layout.capacity > 0 ? `${title} (${layout.capacity})` : title;
        })
        .join(', '),
    [effectiveSetupCodes, setupOptions]
  );
  const isBoardroomOnlySetup =
    setupOptions.length === 1 && setupOptions[0]?.code.toLowerCase() === 'boardroom';
  const currentSpaceAvailability = getAvailabilityBadgeProps(
    isEditing && realTimeAvailability !== undefined
      ? realTimeAvailability
      : selectedPlannerSpace
        ? isSpaceBookable(selectedPlannerSpace)
        : true
  );
  const panelTitle = displayTitle ?? event?.eventTitle ?? 'Untitled Event';

  useEffect(() => {
    if (isPastEvent || !isEditing || !editForm || setupOptions.length === 0) {
      return;
    }

    const validCodeMap = new Map(
      setupOptions.map((layout) => [normalizeSetupCode(layout.code), layout.code])
    );
    const nextCodes = editForm.roomSetupCodes
      .map((code) => validCodeMap.get(normalizeSetupCode(code)))
      .filter((code): code is string => Boolean(code));

    if (isBoardroomOnlySetup) {
      const onlyBoardroomCode = setupOptions[0]?.code;
      if (onlyBoardroomCode && (nextCodes.length !== 1 || nextCodes[0] !== onlyBoardroomCode)) {
        setEditForm((prev) =>
          prev
            ? {
                ...prev,
                roomSetupCodes: [onlyBoardroomCode],
              }
            : prev
        );
      }
      return;
    }

    if (nextCodes.length > 0 && nextCodes.length === editForm.roomSetupCodes.length) {
      return;
    }

    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            roomSetupCodes:
              nextCodes.length > 0 ? nextCodes : setupOptions[0] ? [setupOptions[0].code] : [],
          }
        : prev
    );
  }, [isPastEvent, isEditing, editForm, setupOptions, isBoardroomOnlySetup]);

  const getLayoutsLabel = (layouts: { code: string; capacity: number }[]): string => {
    if (!layouts || layouts.length === 0) {
      return t('reviewPanel.roomLayouts.BOARDROOM');
    }
    return layouts
      .map((l) => {
        const key = `reviewPanel.roomLayouts.${l.code.toUpperCase()}`;
        const translated = t(key);
        return translated !== key ? translated : l.code;
      })
      .join(', ');
  };

  const formatSpaceLabel = (spaceName: string, layouts?: { code: string; capacity: number }[]) => {
    if (layouts !== undefined) {
      const layoutStr = getLayoutsLabel(layouts);
      return layoutStr ? `${spaceName} · ${layoutStr}` : spaceName;
    }
    return spaceName;
  };

  const spaceDisplayValue = (() => {
    if (!event) return '';

    // Use localSelectedSpace when user has picked a different space and spaces are loading
    const effectiveSpace = localSelectedSpace ?? currentSpace;
    if (effectiveSpace) {
      return formatSpaceLabel(effectiveSpace.spaceName ?? '');
    }

    // Use event data directly if available (from getEventById) before falling
    // back to the loading state — avoids showing "Loading spaces..." in read-only mode.
    const eventSpaceName = event.spaces?.[0]?.spaceName ?? event.spaceName ?? '';
    if (eventSpaceName) {
      return formatSpaceLabel(eventSpaceName);
    }

    // When spaces are still loading and we have no local selection, show loading text
    if (isSearchingSpaces) {
      return t('reviewPanel.loadingSpaces');
    }

    return formatSpaceLabel(event.spaceName ?? '');
  })();
  const selectedSpaceCapacityLabel =
    typeof selectedPlannerSpace?.roomCapacity === 'number'
      ? t('reviewPanel.roomCapacityLabel', {
          count: selectedPlannerSpace.roomCapacity,
        })
      : undefined;
  const selectedSpaceCapacityValue =
    typeof selectedPlannerSpace?.roomCapacity === 'number'
      ? String(selectedPlannerSpace.roomCapacity)
      : undefined;

  const handleEditBuildingChange = useCallback(
    (_e: unknown, data: { optionValue?: string }) => {
      const bid = data.optionValue;
      if (!bid || bid === selectedEditBuildingId) return;
      setSelectedEditBuildingId(bid);
      setBuildingSearchQuery(undefined);
      setLocalSelectedSpace(undefined);
      setSpaceQuery(undefined);
      // Reset multi-space selection with one empty row for new room pick
      if (onSelectedSpacesChange) {
        onSelectedSpacesChange([{ spaceId: asSpaceId(''), spaceName: '', selectedSetupCodes: [] }]);
      }
      if (editForm) {
        const { startDate, endDate, startTime, endTime } = editForm;
        onFetchSpaces?.({ startDate, endDate, startTime, endTime, buildingIds: [bid] });
      }
    },
    [selectedEditBuildingId, editForm, onFetchSpaces, onSelectedSpacesChange]
  );

  const buildingDisplayValue = useMemo(
    () => selectedEditBuilding?.buildingName ?? '',
    [selectedEditBuilding]
  );

  const filteredBuildings = useMemo(() => {
    const q = buildingSearchQuery?.trim().toLowerCase() ?? '';
    if (q.length === 0) return buildings ?? [];
    return (buildings ?? []).filter((b) => b.buildingName.toLowerCase().includes(q));
  }, [buildings, buildingSearchQuery]);

  const renderSelectedSpaceSection = () => (
    <div className={styles.spaceSelector}>
      <Field label={t('reviewPanel.building')}>
        <Combobox
          disabled={isPastEvent}
          value={buildingSearchQuery ?? buildingDisplayValue}
          selectedOptions={selectedEditBuildingId ? [selectedEditBuildingId] : []}
          onOptionSelect={handleEditBuildingChange}
          onInput={(e) => setBuildingSearchQuery((e.target as HTMLInputElement).value)}
          onOpenChange={(_e, data) => {
            if (data.open) {
              setBuildingSearchQuery('');
            } else {
              setBuildingSearchQuery(undefined);
            }
          }}
          placeholder={t('reviewPanel.selectBuilding')}
          listbox={{ className: styles.buildingListbox }}
        >
          {filteredBuildings.map((b) => (
            <Option key={b.buildingId} value={b.buildingId} text={b.buildingName}>
              {b.buildingName}
            </Option>
          ))}
          {filteredBuildings.length === 0 && (
            <Option key="__no_match__" value="__no_match__" disabled text="">
              {t('reviewPanel.noSpacesFound')}
            </Option>
          )}
        </Combobox>
      </Field>
      <div className={styles.spaceSelectorHeader}>
        <Text className={styles.spaceSelectorLabel}>
          <span className={styles.fieldLabelWithInfo}>
            {t('reviewPanel.selectedSpace')}
            {isConfirmed && (
              <Tooltip
                content={t('reviewPanel.tooltips.spaceChangeInfo')}
                relationship="description"
                positioning="above"
              >
                <Info16Regular className={styles.spaceInfoIcon} />
              </Tooltip>
            )}
          </span>
        </Text>
      </div>
      <div className={styles.spaceDropdownRow}>
        <div className={styles.spaceInputRelative}>
          <Combobox
            disabled={isPastEvent}
            value={spaceQuery ?? spaceDisplayValue}
            title={spaceDisplayValue}
            selectedOptions={[effectiveSelectedSpaceId]}
            onInput={(e) => setSpaceQuery((e.target as HTMLInputElement).value)}
            onFocus={(e) => {
              const input = e.target as HTMLInputElement;
              requestAnimationFrame(() => {
                input.selectionStart = input.selectionEnd = input.value.length;
                input.scrollLeft = input.scrollWidth;
              });
            }}
            onOptionSelect={(_e, data) => {
              if (data.optionValue) {
                const chosenSpace = (spaces ?? []).find(
                  (item) => item.spaceId === data.optionValue
                );
                setLocalSelectedSpace(chosenSpace);
                setRealTimeAvailability(undefined);
                void onSpaceChange(data.optionValue);
                setSpaceQuery(undefined);
                telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.SPACE_SELECT, {
                  eventId: event?.eventId as string,
                  newSpaceId: data.optionValue,
                });
                // Fetch real-time availability for the newly selected space
                if (chosenSpace && event && editForm) {
                  const { startDate, endDate, startTime, endTime } = editForm;
                  void fetchSpaceAvailability(
                    chosenSpace.spaceId as string,
                    (chosenSpace.buildingId ?? event.buildingId) as string,
                    startDate,
                    endDate,
                    startTime,
                    endTime,
                    event.eventId as string
                  );
                }
              }
            }}
            onBlur={() => setSpaceQuery(undefined)}
            className={mergeClasses(
              styles.comboboxFull,
              isSearchingSpaces && styles.comboboxNonInteractive
            )}
            expandIcon={null}
            listbox={{ className: styles.spaceListbox }}
            placeholder="Search spaces..."
          >
            <div className={styles.spaceOptionGroupHeader}>
              <Text className={styles.spaceOptionGroupLabel}>{t('reviewPanel.roomList')}</Text>
            </div>
            {(() => {
              const filtered = (spaces ?? []).filter((s) => {
                if (spaceQuery === undefined || spaceQuery === '') return true;
                const label = formatSpaceLabel(s.spaceName);
                return label.toLowerCase().includes(spaceQuery.toLowerCase());
              });
              if (filtered.length === 0) {
                return (
                  <div
                    key="__no_results__"
                    role="presentation"
                    style={{ padding: '6px 12px', color: 'var(--colorNeutralForeground3)' }}
                  >
                    {t('reviewPanel.noSpacesFound')}
                  </div>
                );
              }
              return filtered.map((s) => {
                const label = formatSpaceLabel(s.spaceName);
                const optionAvailability = getAvailabilityBadgeProps(isSpaceBookable(s));
                return (
                  <Option key={s.spaceId} value={s.spaceId} text={label}>
                    <span className={styles.spaceOptionContent}>
                      <span>{label}</span>
                      <Badge
                        appearance="filled"
                        color={optionAvailability.color}
                        className={styles.spaceBadgeInline}
                      >
                        {t(optionAvailability.label)}
                      </Badge>
                    </span>
                  </Option>
                );
              });
            })()}
          </Combobox>
          {isCheckingAvailability || isSearchingSpaces ? (
            <Spinner size="tiny" className={styles.badgeOverlay} />
          ) : (
            <Badge
              appearance="filled"
              color={currentSpaceAvailability.color}
              className={styles.badgeOverlay}
            >
              {t(currentSpaceAvailability.label)}
            </Badge>
          )}
        </div>
      </div>
      {selectedSpaceCapacityLabel && (
        <Text className={styles.selectedSpaceCapacity} data-testid="planner-space-capacity">
          {selectedSpaceCapacityLabel}
        </Text>
      )}
      <button className={styles.calendarLink} type="button" onClick={handleViewInCalendarClick}>
        <CalendarLtr20Regular />
        {t('reviewPanel.viewInCalendar')}
        <Open16Regular />
      </button>
    </div>
  );

  return (
    <>
      <OverlayDrawer
        position="end"
        size="medium"
        open={open}
        className={styles.drawerOverride}
        onOpenChange={(_e, data) => {
          if (!data.open) handleClose();
        }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <BasicButton
                variant="subtle"
                size="medium"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                aria-label={t('reviewPanel.close')}
              />
            }
          >
            <div className={styles.headerTitle}>
              {!isLoading && event && (
                <>
                  <Text className={styles.title}>{panelTitle}</Text>
                  <Text className={styles.subtitle}>
                    {t('reviewPanel.requestIdLabel', { id: event.requestId ?? event.eventId })}
                  </Text>
                </>
              )}
            </div>
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody className={styles.body}>
          {isLoading && (
            <div className={styles.loadingContainer} data-testid="review-panel-loading">
              <Spinner size="medium" label={t('reviewPanel.loading')} />
            </div>
          )}

          {event && !isLoading && (
            <>
              {!isEditing && (
                <button
                  className={styles.calendarLink}
                  type="button"
                  onClick={handleViewInCalendarClick}
                >
                  <CalendarLtr20Regular />
                  {t('reviewPanel.viewInCalendar')}
                  <Open16Regular />
                </button>
              )}

              {/* Employee Intake Form */}
              <div className={styles.sectionHeader}>
                <Text className={styles.sectionTitle}>{t('reviewPanel.intakeForm.title')}</Text>
                {isEditable && !isEditing && isAssignedPlanner && (
                  <BasicButton
                    variant="subtle"
                    icon={<Edit20Regular />}
                    size="small"
                    className={styles.editButton}
                    onClick={handleEditClick}
                  >
                    {t('reviewPanel.intakeForm.edit')}
                  </BasicButton>
                )}
                {isEditing && (
                  <div className={styles.editActionsRow}>
                    <BasicButton variant="subtle" size="small" onClick={handleCancelEdit}>
                      Cancel
                    </BasicButton>
                    {!isConfirmed && (
                      <BasicButton
                        variant="primary"
                        icon={<Save20Regular />}
                        size="small"
                        onClick={() => void handleSaveEdit()}
                        disabled={isSavingEdit}
                      >
                        Save
                      </BasicButton>
                    )}
                  </div>
                )}
              </div>

              {isEditing && hasEditValidationErrors && (
                <div
                  className={styles.capacityWarningBanner}
                  data-testid="validation-error-summary"
                  role="alert"
                >
                  <span className={styles.capacityWarningIcon} aria-hidden="true">
                    <ErrorCircle16Filled />
                  </span>
                  <span>{t('reviewPanel.validation.summary')}</span>
                </div>
              )}

              {!isEditing ? (
                <>
                  <div className={styles.fieldGrid}>
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.stateProvince')}
                      value={stateProvinceValue}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.dateTime')}
                      value={formatDateRange(
                        event.eventStartDate,
                        event.eventEndDate,
                        i18n.language,
                        building?.gmtZone
                      )}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.building')}
                      value={
                        building?.buildingName ??
                        event.building?.buildingName ??
                        event.buildingName ??
                        ''
                      }
                    />
                    {event.spaces && event.spaces.length >= 1 ? (
                      <>
                        <div className={styles.multiSpaceHeader}>
                          <Text weight="semibold">{t('reviewPanel.selectedSpaces')}</Text>
                          <Badge appearance="filled" color="informative">
                            {t('reviewPanel.selectedSpacesCount', { count: event.spaces.length })}
                          </Badge>
                        </div>
                        <div className={styles.multiSpaceList}>
                          {(showAllSpaces ? event.spaces : event.spaces.slice(0, 5)).map(
                            (s, idx) => {
                              const spaceData = (spaces ?? []).find(
                                (sp) => sp.spaceId === s.spaceId
                              );
                              const roomCap = spaceData?.roomCapacity ?? 0;
                              const setupLabel = (s.selectedSetupCodes ?? [])
                                .map((code) => {
                                  const meta = getRoomLayoutMeta(code);
                                  const layout = spaceData?.supportedRoomLayouts?.find(
                                    (l) => l.code.toLowerCase() === code.toLowerCase()
                                  );
                                  const layoutCap = layout?.capacity ?? 0;
                                  const cap = layoutCap > 0 ? layoutCap : roomCap;
                                  return cap > 0 ? `${meta.title} (${cap})` : meta.title;
                                })
                                .join(', ');
                              const boardroomLabel =
                                roomCap > 0 ? `Boardroom (${roomCap})` : 'Boardroom';
                              return (
                                <div key={`${s.spaceId}-${idx}`} className={styles.multiSpaceItem}>
                                  {`${s.spaceName} (Capacity: ${setupLabel || boardroomLabel})`}
                                </div>
                              );
                            }
                          )}
                          {event.spaces.length > 5 && (
                            <Link
                              onClick={() => setShowAllSpaces(!showAllSpaces)}
                              className={styles.showMoreLink}
                            >
                              {showAllSpaces
                                ? t('reviewPanel.showLess')
                                : t('reviewPanel.showMore', { count: event.spaces.length - 5 })}
                            </Link>
                          )}
                        </div>
                        <Text className={styles.multiSpaceSummary}>
                          {t('reviewPanel.spacesSelectedSummary', {
                            count: event.spaces.length,
                            capacity: event.spaces.reduce((total, s) => {
                              const sd = (spaces ?? []).find((sp) => sp.spaceId === s.spaceId);
                              const codes = s.selectedSetupCodes ?? [];
                              if (codes.length === 0) return total + (sd?.roomCapacity ?? 0);
                              const setupSum = codes.reduce((sum, code) => {
                                const layout = sd?.supportedRoomLayouts?.find(
                                  (l) => l.code.toLowerCase() === code.toLowerCase()
                                );
                                const cap = layout?.capacity ?? 0;
                                return sum + (cap > 0 ? cap : (sd?.roomCapacity ?? 0));
                              }, 0);
                              return total + setupSum;
                            }, 0),
                          })}
                        </Text>
                      </>
                    ) : (
                      <InfoRow
                        layout="inline"
                        label={t('reviewPanel.selectedSpace')}
                        value={spaceDisplayValue}
                      />
                    )}
                    {!(event.spaces && event.spaces.length >= 1) && selectedSpaceCapacityValue && (
                      <InfoRow
                        layout="inline"
                        label={t('reviewPanel.roomCapacity')}
                        value={selectedSpaceCapacityValue}
                        testId="planner-space-capacity"
                      />
                    )}
                    {!(event.spaces && event.spaces.length >= 1) && (
                      <InfoRow
                        layout="inline"
                        label={t('reviewPanel.fields.roomSetupPreference')}
                        value={roomSetupDisplayValue || '—'}
                      />
                    )}
                  </div>

                  <div className={styles.fieldGrid}>
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.attendees')}
                      value={`${event.numberOfAttendees} attendees`}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.attendeeType')}
                      value={getAttendeeTypeLabel(event.attendeeTypeCode)}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.businessPurpose')}
                      value={getVisitPurposeLabel(event.visitPurposeCode)}
                    />
                    {showBusinessGroup && (
                      <InfoRow
                        layout="inline"
                        label={t('reviewPanel.fields.businessGroup')}
                        value={getBusinessGroupLabel(event.businessGroupCode, building)}
                      />
                    )}
                    {showBusinessGroup && (
                      <InfoRow
                        layout="inline"
                        label={t('reviewPanel.fields.businessJustification')}
                        value={event.businessJustification || '—'}
                      />
                    )}
                    {showCostCenter && (
                      <InfoRow
                        layout="inline"
                        label={t('reviewPanel.fields.ioCcNumber')}
                        value={event.sponsor?.costCenter ?? undefined}
                      />
                    )}
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.onSiteContacts')}
                      value={event.onSiteContacts?.map((c) => `${c.name}, ${c.email}`).join('; ')}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.catering')}
                      value={yesNo(event.hasCatering)}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.filming')}
                      value={yesNo(event.filmingRequired)}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.overnightStorage')}
                      value={yesNo(event.overnightStorageRequired)}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.shipments')}
                      value={yesNo(event.shipmentsExpected)}
                    />
                    <InfoRow
                      layout="inline"
                      label={t('reviewPanel.fields.additionalInformation')}
                      value={event.additionalInformation || '—'}
                    />
                  </div>
                </>
              ) : (
                editForm && (
                  <div className={styles.editFormGrid}>
                    <Field label="State/province" required>
                      <Input value={editForm.stateProvince} disabled />
                    </Field>
                    <Field
                      label={
                        isConfirmed ? (
                          <span className={styles.fieldLabelWithInfo}>
                            When
                            <Tooltip
                              content={t('reviewPanel.tooltips.dateChangeInfo')}
                              relationship="description"
                              positioning="above"
                            >
                              <Info16Regular className={styles.infoIcon} />
                            </Tooltip>
                          </span>
                        ) : (
                          'When'
                        )
                      }
                      required
                      validationState={editFormValidationErrors.startDate ? 'error' : 'none'}
                      validationMessage={editFormValidationErrors.startDate}
                    >
                      <Input
                        type="date"
                        disabled={isPastEvent}
                        value={editForm.startDate}
                        onChange={(_e, d) =>
                          setEditForm({ ...editForm, startDate: d.value, endDate: d.value })
                        }
                      />
                    </Field>
                    <Field
                      label={t('reviewPanel.fields.duration')}
                      required
                      validationState={editFormValidationErrors.duration ? 'error' : 'none'}
                      validationMessage={editFormValidationErrors.duration}
                    >
                      <div className={styles.timeRangeRow}>
                        <TimePicker
                          placeholder="Select time"
                          disabled={isPastEvent}
                          value={editForm.startTime}
                          input={{ 'aria-label': 'Duration start' }}
                          onTimeStringChange={(startTime) =>
                            setEditForm((prev) => {
                              if (!prev) {
                                return prev;
                              }

                              const shouldClearEnd =
                                startTime && prev.endTime && prev.endTime <= startTime;

                              return {
                                ...prev,
                                startTime,
                                endTime: shouldClearEnd ? '' : prev.endTime,
                              };
                            })
                          }
                        />
                        <Text className={styles.durationSeparator}>—</Text>
                        <TimePicker
                          placeholder="Select time"
                          disabled={isPastEvent}
                          value={editForm.endTime}
                          startHour={
                            editForm.startTime
                              ? (parseInt(editForm.startTime.split(':')[0] ?? '0', 10) as 0)
                              : 0
                          }
                          input={{ 'aria-label': 'Duration end' }}
                          onTimeStringChange={(endTime) => setEditForm({ ...editForm, endTime })}
                        />
                      </div>
                    </Field>

                    {selectedSpaces && onSelectedSpacesChange ? (
                      <>
                        <Field label={t('reviewPanel.building')}>
                          <Combobox
                            disabled={isPastEvent}
                            value={buildingSearchQuery ?? buildingDisplayValue}
                            selectedOptions={selectedEditBuildingId ? [selectedEditBuildingId] : []}
                            onOptionSelect={handleEditBuildingChange}
                            onInput={(e) =>
                              setBuildingSearchQuery((e.target as HTMLInputElement).value)
                            }
                            onOpenChange={(_e, data) => {
                              if (data.open) {
                                setBuildingSearchQuery('');
                              } else {
                                setBuildingSearchQuery(undefined);
                              }
                            }}
                            placeholder={t('reviewPanel.selectBuilding')}
                            listbox={{ className: styles.buildingListbox }}
                          >
                            {filteredBuildings.map((b) => (
                              <Option key={b.buildingId} value={b.buildingId} text={b.buildingName}>
                                {b.buildingName}
                              </Option>
                            ))}
                            {filteredBuildings.length === 0 && (
                              <Option key="__no_match__" value="__no_match__" disabled text="">
                                {t('reviewPanel.noSpacesFound')}
                              </Option>
                            )}
                          </Combobox>
                        </Field>
                        <SelectedSpacesEditor
                          spaces={selectedSpaces}
                          availableSpaces={spaces ?? []}
                          attendeeCount={Number.parseInt(editForm.attendees, 10) || 0}
                          onChange={onSelectedSpacesChange}
                          disabled={!isEditing}
                          locationLocked={isPastEvent}
                          isLoading={isSearchingSpaces}
                          onViewCalendar={handleViewInCalendarClick}
                          hasUnsavedChanges={hasUnsavedChanges}
                        />
                      </>
                    ) : (
                      renderSelectedSpaceSection()
                    )}

                    <Field
                      label={t('reviewPanel.fields.attendees')}
                      required
                      validationState={editFormValidationErrors.attendees ? 'error' : 'none'}
                      validationMessage={editFormValidationErrors.attendees}
                    >
                      <Input
                        type="number"
                        disabled={isPastEvent}
                        value={editForm.attendees}
                        onChange={(_e, d) => setEditForm({ ...editForm, attendees: d.value })}
                      />
                    </Field>
                    {!(selectedSpaces && onSelectedSpacesChange) && (
                      <Field
                        label={t('reviewPanel.fields.roomSetupPreference')}
                        required
                        validationState={editFormValidationErrors.roomSetup ? 'error' : 'none'}
                        validationMessage={editFormValidationErrors.roomSetup}
                      >
                        <Dropdown
                          disabled={isPastEvent}
                          multiselect={!isBoardroomOnlySetup}
                          value={
                            editForm.roomSetupCodes.length > 0
                              ? editForm.roomSetupCodes
                                  .map((code) => {
                                    const title = getRoomLayoutMeta(code).title;
                                    const layout = setupOptions.find(
                                      (opt) =>
                                        normalizeSetupCode(opt.code) === normalizeSetupCode(code)
                                    );
                                    return layout && layout.capacity > 0
                                      ? `${title} (${layout.capacity})`
                                      : title;
                                  })
                                  .join(', ')
                              : ''
                          }
                          selectedOptions={editForm.roomSetupCodes}
                          onOptionSelect={(_e, d) => {
                            const selectedOptions = d.selectedOptions;
                            if (isBoardroomOnlySetup) {
                              const onlyOption = d.optionValue ?? setupOptions[0]?.code ?? '';
                              setEditForm({
                                ...editForm,
                                roomSetupCodes: onlyOption ? [onlyOption] : [],
                              });
                              return;
                            }

                            setEditForm({
                              ...editForm,
                              roomSetupCodes: selectedOptions.length > 0 ? selectedOptions : [],
                            });
                          }}
                        >
                          {setupOptions.map((layout) => {
                            const meta = getRoomLayoutMeta(layout.code);
                            const label =
                              layout.capacity > 0
                                ? `${meta.title} (${layout.capacity})`
                                : meta.title;
                            return (
                              <Option key={layout.code} value={layout.code} text={meta.title}>
                                {label}
                              </Option>
                            );
                          })}
                        </Dropdown>
                      </Field>
                    )}
                    <Field
                      label={t('reviewPanel.fields.attendeeType')}
                      required
                      validationState={editFormValidationErrors.attendeeType ? 'error' : 'none'}
                      validationMessage={editFormValidationErrors.attendeeType}
                    >
                      <Dropdown
                        value={getAttendeeTypeLabel(editForm.attendeeType)}
                        selectedOptions={[editForm.attendeeType]}
                        onOptionSelect={(_e, d) =>
                          setEditForm({ ...editForm, attendeeType: d.optionValue ?? '' })
                        }
                      >
                        {ATTENDEE_TYPE_OPTIONS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.labelKey}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field
                      label="Business purpose"
                      required
                      validationState={editFormValidationErrors.businessPurpose ? 'error' : 'none'}
                      validationMessage={editFormValidationErrors.businessPurpose}
                    >
                      <Dropdown
                        value={getVisitPurposeLabel(editForm.businessPurpose)}
                        selectedOptions={[editForm.businessPurpose]}
                        onOptionSelect={(_e, d) =>
                          setEditForm({ ...editForm, businessPurpose: d.optionValue ?? '' })
                        }
                      >
                        {VISIT_PURPOSE_OPTIONS.map((option) => (
                          <Option key={option.value} value={option.value} text={option.labelKey}>
                            {option.labelKey}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    {showBusinessGroup && (
                      <Field
                        label={t('reviewPanel.fields.businessGroup')}
                        required
                        validationState={editFormValidationErrors.businessGroup ? 'error' : 'none'}
                        validationMessage={editFormValidationErrors.businessGroup}
                      >
                        <Dropdown
                          value={getBusinessGroupLabel(editForm.businessGroupCode, building)}
                          selectedOptions={
                            editForm.businessGroupCode ? [editForm.businessGroupCode] : []
                          }
                          onOptionSelect={(_e, d) =>
                            setEditForm({ ...editForm, businessGroupCode: d.optionValue ?? '' })
                          }
                        >
                          {businessGroupOptions.map((group) => (
                            <Option key={group.code} value={group.code} text={group.label}>
                              {group.label}
                            </Option>
                          ))}
                        </Dropdown>
                      </Field>
                    )}
                    {showBusinessGroup && (
                      <Field
                        label={t('reviewPanel.fields.businessJustification')}
                        required
                        validationState={
                          editFormValidationErrors.businessJustification ? 'error' : 'none'
                        }
                        validationMessage={editFormValidationErrors.businessJustification}
                      >
                        <Input
                          value={editForm.businessJustification}
                          onChange={(_e, d) =>
                            setEditForm({ ...editForm, businessJustification: d.value })
                          }
                        />
                      </Field>
                    )}
                    {showCostCenter && (
                      <Field
                        label="IO/CC Number (Cost Center)"
                        required
                        validationState={editFormValidationErrors.ioCcNumber ? 'error' : 'none'}
                        validationMessage={editFormValidationErrors.ioCcNumber}
                      >
                        <Input
                          value={editForm.ioCcNumber}
                          onChange={(_e, d) => setEditForm({ ...editForm, ioCcNumber: d.value })}
                        />
                      </Field>
                    )}
                    <Field label="Do you want to add an onsite contact?">
                      <RadioGroup
                        layout="horizontal"
                        value={editForm.hasOnSiteContact ? 'yes' : 'no'}
                        onChange={(_e, d) => {
                          const wantsContact = d.value === 'yes';
                          setEditForm({
                            ...editForm,
                            hasOnSiteContact: wantsContact,
                            onSiteContacts: wantsContact
                              ? editForm.onSiteContacts.length > 0 &&
                                editForm.onSiteContacts.some((c) => c.name || c.email)
                                ? editForm.onSiteContacts
                                : [{ name: '', email: '' }]
                              : [{ name: '', email: '' }],
                          });
                        }}
                      >
                        <Radio value="yes" label="Yes" />
                        <Radio value="no" label="No" />
                      </RadioGroup>
                    </Field>
                    {editForm.hasOnSiteContact && (
                      <div className={styles.onSiteContactsGroup}>
                        <div
                          className={
                            editForm.onSiteContacts.length > 1
                              ? styles.onSiteContactHeader
                              : `${styles.onSiteContactHeader} ${styles.onSiteContactHeaderSingle}`
                          }
                        >
                          <span className={styles.onSiteContactHeaderLabel}>
                            Contact name
                            <span className={styles.requiredMark} aria-hidden="true">
                              {' '}
                              *
                            </span>
                          </span>
                          <span className={styles.onSiteContactHeaderLabel}>
                            Email address
                            <span className={styles.requiredMark} aria-hidden="true">
                              {' '}
                              *
                            </span>
                          </span>
                        </div>
                        {editForm.onSiteContacts.map((contact, idx) => (
                          <div
                            key={idx}
                            className={
                              editForm.onSiteContacts.length > 1
                                ? styles.onSiteContactRow
                                : `${styles.onSiteContactRow} ${styles.onSiteContactRowSingle}`
                            }
                          >
                            <Field
                              validationState={
                                editFormValidationErrors[`onSiteContactName-${idx}`]
                                  ? 'error'
                                  : 'none'
                              }
                              validationMessage={
                                editFormValidationErrors[`onSiteContactName-${idx}`]
                              }
                            >
                              <Input
                                value={contact.name}
                                placeholder="Enter name"
                                aria-label="Contact name"
                                autoComplete="name"
                                onChange={(_e, d) => {
                                  const updated = editForm.onSiteContacts.map((c, i) =>
                                    i === idx ? { ...c, name: d.value } : c
                                  );
                                  setEditForm({ ...editForm, onSiteContacts: updated });
                                }}
                              />
                            </Field>
                            <Field
                              validationState={
                                editFormValidationErrors[`onSiteContactEmail-${idx}`]
                                  ? 'error'
                                  : 'none'
                              }
                              validationMessage={
                                editFormValidationErrors[`onSiteContactEmail-${idx}`]
                              }
                            >
                              <Input
                                type="email"
                                value={contact.email}
                                placeholder="Enter email"
                                aria-label="Email address"
                                autoComplete="email"
                                onChange={(_e, d) => {
                                  const updated = editForm.onSiteContacts.map((c, i) =>
                                    i === idx ? { ...c, email: d.value } : c
                                  );
                                  setEditForm({ ...editForm, onSiteContacts: updated });
                                }}
                              />
                            </Field>
                            {editForm.onSiteContacts.length > 1 && (
                              <BasicButton
                                variant="subtle"
                                size="small"
                                icon={<Dismiss24Regular />}
                                aria-label="Remove contact"
                                onClick={() => {
                                  const updated = editForm.onSiteContacts.filter(
                                    (_, i) => i !== idx
                                  );
                                  setEditForm({
                                    ...editForm,
                                    onSiteContacts:
                                      updated.length > 0 ? updated : [{ name: '', email: '' }],
                                  });
                                }}
                              />
                            )}
                          </div>
                        ))}
                        <BasicButton
                          variant="primary"
                          size="small"
                          style={{ alignSelf: 'flex-start' }}
                          disabled={editForm.onSiteContacts.length >= MAX_ONSITE_CONTACTS}
                          onClick={() =>
                            setEditForm({
                              ...editForm,
                              onSiteContacts: [...editForm.onSiteContacts, { name: '', email: '' }],
                            })
                          }
                        >
                          Add more onsite contacts
                        </BasicButton>
                      </div>
                    )}
                    <Field label="Will this event have catering?" required>
                      <RadioGroup
                        layout="horizontal"
                        value={editForm.hasCatering ? 'yes' : 'no'}
                        onChange={(_e, d) =>
                          setEditForm({ ...editForm, hasCatering: d.value === 'yes' })
                        }
                      >
                        <Radio value="yes" label="Yes" />
                        <Radio value="no" label="No" />
                      </RadioGroup>
                    </Field>
                    <Field label="Will this event be filmed?" required>
                      <RadioGroup
                        layout="horizontal"
                        value={editForm.filmingRequired ? 'yes' : 'no'}
                        onChange={(_e, d) =>
                          setEditForm({ ...editForm, filmingRequired: d.value === 'yes' })
                        }
                      >
                        <Radio value="yes" label="Yes" />
                        <Radio value="no" label="No" />
                      </RadioGroup>
                    </Field>
                    <Field label="Overnight storage needed?" required>
                      <RadioGroup
                        layout="horizontal"
                        value={editForm.overnightStorage ? 'yes' : 'no'}
                        onChange={(_e, d) =>
                          setEditForm({ ...editForm, overnightStorage: d.value === 'yes' })
                        }
                      >
                        <Radio value="yes" label="Yes" />
                        <Radio value="no" label="No" />
                      </RadioGroup>
                    </Field>
                    <Field label="Any shipments expected?" required>
                      <RadioGroup
                        layout="horizontal"
                        value={editForm.shipments ? 'yes' : 'no'}
                        onChange={(_e, d) =>
                          setEditForm({ ...editForm, shipments: d.value === 'yes' })
                        }
                      >
                        <Radio value="yes" label="Yes" />
                        <Radio value="no" label="No" />
                      </RadioGroup>
                    </Field>
                    <Field label={t('reviewPanel.fields.additionalInformation')}>
                      <Textarea
                        value={editForm.additionalInformation}
                        onChange={(_e, d) =>
                          setEditForm({ ...editForm, additionalInformation: d.value })
                        }
                        resize="vertical"
                      />
                    </Field>
                  </div>
                )
              )}

              <div className={styles.divider} />

              {/* Sponsor & Planner */}
              <div className={styles.personSection}>
                <Text className={styles.personLabel}>
                  {t('reviewPanel.sponsor')}
                  {isEditing && (
                    <span className={styles.requiredMark} aria-hidden="true">
                      {' '}
                      *
                    </span>
                  )}
                </Text>
                {isEditing && editForm ? (
                  <PeoplePicker
                    value={editForm.sponsorName}
                    onChange={(_key: string, value: unknown) => {
                      if (_key === 'sponsor.name') {
                        setEditForm((prev) =>
                          prev ? { ...prev, sponsorName: value as string } : prev
                        );
                      } else if (_key === 'sponsor.email') {
                        setEditForm((prev) =>
                          prev ? { ...prev, sponsorEmail: value as string } : prev
                        );
                      }
                    }}
                    disabled={false}
                    required
                    error={editFormValidationErrors.sponsor}
                  />
                ) : (
                  event.sponsor && (
                    <div className={styles.personChip}>
                      <Avatar name={event.sponsor.name} size={24} color="colorful" />
                      <Text className={styles.personName}>{event.sponsor.name}</Text>
                    </div>
                  )
                )}
              </div>

              <div className={styles.personSection}>
                <Text className={styles.personLabel}>{t('reviewPanel.planner')}</Text>
                {(event as { plannerAssignedName?: string }).plannerAssignedName && (
                  <div className={styles.personChip}>
                    <Avatar
                      name={(event as { plannerAssignedName?: string }).plannerAssignedName ?? ''}
                      size={24}
                      color="colorful"
                    />
                    <Text className={styles.personName}>
                      {(event as { plannerAssignedName?: string }).plannerAssignedName}
                    </Text>
                  </div>
                )}
              </div>

              <div className={styles.divider} />

              {/* Activities Timeline */}
              <button
                className={styles.activitiesToggle}
                onClick={() => setActivitiesExpanded((prev) => !prev)}
                type="button"
                aria-expanded={activitiesExpanded}
              >
                {activitiesExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                {t('reviewPanel.activities.title')}
              </button>
              {activitiesExpanded && (
                <div className={styles.activitiesList} data-testid="activities-list">
                  <div ref={activitiesSentinelRef} />
                  {(historyLoading || isHistoryLoading || !historyFetched) &&
                    !isHistoryError &&
                    historyEntries.length === 0 && (
                      <Spinner
                        size="tiny"
                        label={t('reviewPanel.activities.loading')}
                        data-testid="activities-loading"
                      />
                    )}
                  {isHistoryError && historyEntries.length === 0 && (
                    <Text className={styles.activityMeta} data-testid="activities-error">
                      {t('reviewPanel.activities.error')}
                    </Text>
                  )}
                  {historyEntries.length > 0
                    ? (() => {
                        const grouped: Record<
                          string,
                          { displayLabel: string; entries: EventHistoryEntry[] }
                        > = {};
                        for (const entry of historyEntries) {
                          const dateKey = getIsoDateInTimeZone(entry.changedAt, eventTimeZone);
                          if (!grouped[dateKey]) {
                            grouped[dateKey] = {
                              displayLabel: formatUtcDateInTimeZone(entry.changedAt, eventTimeZone),
                              entries: [],
                            };
                          }
                          grouped[dateKey].entries.push(entry);
                        }
                        const sortedDates = Object.keys(grouped).sort().reverse();
                        return (
                          <>
                            {sortedDates.map((dateKey) => (
                              <div key={dateKey} className={styles.activityDateGroup}>
                                <Text className={styles.activityDateHeader}>
                                  {grouped[dateKey]?.displayLabel ?? dateKey}
                                </Text>
                                {(grouped[dateKey]?.entries ?? []).map((entry, idx) => {
                                  const actionKey = `reviewPanel.activities.actions.${entry.actionType}`;
                                  const actionVerb =
                                    t(actionKey) !== actionKey ? t(actionKey) : entry.actionType;
                                  const spaceChange = deriveSpaceChange(entry.fieldChanges, t);
                                  return (
                                    <div
                                      className={styles.activityTimelineItem}
                                      key={`${entry.eventHistoryId}-${idx}`}
                                    >
                                      <Avatar
                                        name={
                                          entry.actionType === 'Handoff' ||
                                          entry.actionType === 'BatchHandoff'
                                            ? (entry.fieldChanges?.find(
                                                (fc) => fc.fieldName === 'PlannerAssignedName'
                                              )?.newValue ?? entry.changedBy)
                                            : entry.changedBy
                                        }
                                        size={24}
                                        color="colorful"
                                      />
                                      <div className={styles.activityContent}>
                                        <div className={styles.activityTitleRow}>
                                          <Text className={styles.activityAction}>
                                            <Text weight="semibold">
                                              {entry.actionType === 'Handoff' ||
                                              entry.actionType === 'BatchHandoff'
                                                ? (entry.fieldChanges?.find(
                                                    (fc) => fc.fieldName === 'PlannerAssignedName'
                                                  )?.newValue ?? entry.changedBy)
                                                : entry.changedBy}
                                            </Text>{' '}
                                            {actionVerb}
                                          </Text>
                                          <Text className={styles.activityTime}>
                                            {formatUtcTimeInTimeZone(
                                              entry.changedAt,
                                              'en-US',
                                              eventTimeZone,
                                              { hour: 'numeric', minute: '2-digit', hour12: true }
                                            )}
                                          </Text>
                                        </div>
                                        {(entry.actionType === 'Handoff' ||
                                          entry.actionType === 'BatchHandoff') &&
                                          entry.fieldChanges?.some(
                                            (fc) => fc.fieldName === 'PlannerAssignedName'
                                          ) && (
                                            <div className={styles.activityFieldChanges}>
                                              <Text className={styles.activityDescription}>
                                                {t('reviewPanel.activities.handoffDetail', {
                                                  previousPlanner: entry.changedBy,
                                                })}
                                              </Text>
                                            </div>
                                          )}
                                        {entry.actionType === 'Claim' ||
                                        entry.actionType === 'BatchClaim' ||
                                        entry.actionType === 'Handoff' ||
                                        entry.actionType === 'BatchHandoff'
                                          ? entry.fieldChanges?.some(
                                              (fc) => fc.fieldName === 'PlannerAssignedEmail'
                                            ) && (
                                              <div className={styles.activityFieldChanges}>
                                                {entry.fieldChanges
                                                  .filter(
                                                    (fc) => fc.fieldName === 'PlannerAssignedEmail'
                                                  )
                                                  .map((fc, fcIdx) => (
                                                    <Text
                                                      key={fcIdx}
                                                      className={styles.activityDescription}
                                                    >
                                                      {formatFieldChangeDetail(
                                                        fc,
                                                        t,
                                                        eventTimeZone
                                                      )}
                                                    </Text>
                                                  ))}
                                              </div>
                                            )
                                          : entry.fieldChanges &&
                                            entry.fieldChanges.length > 0 && (
                                              <div className={styles.activityFieldChanges}>
                                                {spaceChange &&
                                                  renderSpaceChange(spaceChange, styles)}
                                                {entry.fieldChanges
                                                  .filter(shouldDisplayFieldChange)
                                                  .map((fc, fcIdx) => (
                                                    <Text
                                                      key={fcIdx}
                                                      className={styles.activityDescription}
                                                    >
                                                      {formatFieldChangeDetail(
                                                        fc,
                                                        t,
                                                        eventTimeZone
                                                      )}
                                                    </Text>
                                                  ))}
                                              </div>
                                            )}
                                        {entry.comments &&
                                          entry.actionType !== 'Decline' &&
                                          entry.actionType !== 'Cancel' &&
                                          entry.actionType !== 'RequestCancellation' &&
                                          entry.actionType !== 'ConfirmCancellation' && (
                                            <Text className={styles.activityDescription}>
                                              {entry.comments}
                                            </Text>
                                          )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                            {historyHasMore && (
                              <div ref={loadMoreSentinelRef} data-testid="activities-load-more">
                                {isHistoryFetching && (
                                  <Spinner
                                    size="tiny"
                                    label={t('reviewPanel.activities.loading')}
                                  />
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()
                    : historyFetched &&
                      !historyLoading &&
                      !isHistoryLoading &&
                      !isHistoryError && (
                        <Text className={styles.activityMeta} data-testid="activities-empty">
                          {t('reviewPanel.activities.empty')}
                        </Text>
                      )}
                </div>
              )}
            </>
          )}
        </DrawerBody>

        <DrawerFooter className={styles.footer}>
          {!isLoading && isConfirmed && isEditing && isAssignedPlanner && (
            <BasicButton
              variant="primary"
              size="medium"
              onClick={() => {
                telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
                  action: 'UpdateButtonClicked',
                  eventId: (event?.eventId as string) ?? '',
                });
                void handleSaveEdit();
              }}
              disabled={isSavingEdit}
            >
              {t('reviewPanel.footer.update')}
            </BasicButton>
          )}
          {!isLoading &&
            isActionable &&
            !isPastEvent &&
            !isConfirmed &&
            !isEditing &&
            isAssignedPlanner && (
              <>
                <BasicButton
                  variant="primary"
                  size="medium"
                  icon={isConfirming ? <Spinner size="tiny" /> : undefined}
                  onClick={() => {
                    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
                      action: 'ConfirmClicked',
                      eventId: (event?.eventId as string) ?? '',
                    });
                    onConfirm();
                  }}
                  disabled={!isActionable || isConfirming}
                >
                  {t('reviewPanel.footer.confirm')}
                </BasicButton>
                <BasicButton
                  variant="outline"
                  size="medium"
                  onClick={() => {
                    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
                      action: 'DeclineClicked',
                      eventId: (event?.eventId as string) ?? '',
                    });
                    onDeclineClick();
                  }}
                  disabled={!isActionable}
                >
                  {t('reviewPanel.footer.decline')}
                </BasicButton>
              </>
            )}
          {!isLoading && isCancellable && !isEditing && onCancelClick && isAssignedPlanner && (
            <BasicButton
              variant="outline"
              size="medium"
              onClick={() => {
                telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
                  action: 'CancelBookingClicked',
                  eventId: (event?.eventId as string) ?? '',
                });
                onCancelClick();
              }}
            >
              {t('reviewPanel.footer.cancelBooking')}
            </BasicButton>
          )}
          {!isLoading &&
            isNoOccurrenceEligible &&
            !isEditing &&
            onNoOccurrenceClick &&
            isAssignedPlanner && (
              <BasicButton
                variant="outline"
                size="medium"
                onClick={() => {
                  telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
                    action: 'NoOccurrenceClicked',
                    eventId: (event?.eventId as string) ?? '',
                  });
                  setNoOccurrenceDialogOpen(true);
                }}
                disabled={isMarkingNoOccurrence}
              >
                {t('reviewPanel.footer.noOccurrence')}
              </BasicButton>
            )}
          {!isLoading && canResendSurvey && onResendSurvey && (
            <>
              <BasicButton
                variant="outline"
                size="medium"
                onClick={handleResendSurvey}
                disabled={isResendingSurvey}
              >
                {isResendingSurvey ? <Spinner size="tiny" /> : t('reviewPanel.footer.resendSurvey')}
              </BasicButton>
            </>
          )}
        </DrawerFooter>
      </OverlayDrawer>
      <Dialog
        open={noOccurrenceDialogOpen}
        modalType="modal"
        onOpenChange={(_, data) => {
          if (!data.open && !isMarkingNoOccurrence) {
            setNoOccurrenceDialogOpen(false);
          }
        }}
      >
        <DialogSurface aria-describedby="no-occurrence-dialog-description">
          <DialogBody>
            <DialogTitle>{t('dialogs.noOccurrence.title')}</DialogTitle>
            <DialogContent id="no-occurrence-dialog-description">
              {t('dialogs.noOccurrence.description')}
            </DialogContent>
            <DialogActions>
              <BasicButton
                variant="secondary"
                size="medium"
                onClick={() => setNoOccurrenceDialogOpen(false)}
                disabled={isMarkingNoOccurrence}
              >
                {t('dialogs.noOccurrence.cancel')}
              </BasicButton>
              <BasicButton
                variant="primary"
                size="medium"
                onClick={() => {
                  setNoOccurrenceDialogOpen(false);
                  onNoOccurrenceClick?.();
                }}
                disabled={isMarkingNoOccurrence}
              >
                {t('dialogs.noOccurrence.proceed')}
              </BasicButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

EventReviewPanel.displayName = 'EventReviewPanel';
